import { SpiralLoading as Loading } from 'component/base/Loading';
import { Design, Intent } from 'enums';
import { useContext as useEventTrackerContext } from 'providers/EventTracker';
import { EventActionConfigKey } from 'providers/EventTracker/initialContext';
import React, { useCallback } from 'react';

import { Content, DefaultButton, LoadingContainer } from './styles';

type ButtonElementProps = React.ButtonHTMLAttributes<HTMLButtonElement>;
export interface IProps {
  /**
   * Determines if the button should render full width
   */
  block?: boolean;
  /**
   * Content to be rendered inside the component
   */
  children: React.ReactNode;
  /**
   * The Design of the button
   *  @default Design.STANDARD
   */
  design?: Design;
  /**
   * The Intent of the button (Primary, warning, success etc)
   *  @default Intent.NONE
   */
  intent?: Intent;
  /**
   * @deprecated use {@param disabled} instead.
   *
   * Flag indicating that clicking on the button is currently invalid (e.g. Form Validation failing)
   */
  invalid?: boolean;
  /**
   * Flag indicating that the content the button relates to is being submitted (e.g. Form Submission)
   */
  submitting?: boolean;
  /**
   *  BUtton is unclickable
   */
  disabled?: boolean;
  /**
   * A key that used to get an event tracker config - see {@link providers/EventTracker/initialContext}
   * This config sets whether a click event is logged in google analytics
   */
  eventTrackerConfigKey?: EventActionConfigKey;
  /**
   * Click event to be fired when the user clicks on the button.
   */
  onClick?(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
  type?: 'button' | 'submit' | 'reset';
}

export type ButtonProps = IProps & ButtonElementProps;

/**
 * Main button component.
 *
 * Styling options accessible using Design and Intent enums:
 *  - import { Design, Intent } from 'enums';
 */
const Button: React.FunctionComponent<IProps & ButtonElementProps> = ({
  block = false,
  children,
  intent = Intent.NONE,
  invalid = false,
  onClick,
  submitting = false,
  disabled = false,
  design = Design.STANDARD,
  eventTrackerConfigKey,
  type,
  ...props
}: IProps & ButtonElementProps) => {
  const { onClickTrackEvent, onSubmitTrackEvent } = useEventTrackerContext();

  const onClickWrapped = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (type !== 'submit') {
        onClickTrackEvent({ eventTrackerConfigKey });
      } else {
        onSubmitTrackEvent({ eventTrackerConfigKey });
      }
      if (onClick) {
        onClick(event);
      }
    },
    [
      onSubmitTrackEvent,
      type,
      onClickTrackEvent,
      onClick,
      eventTrackerConfigKey,
    ],
  );

  return (
    <DefaultButton
      $block={block}
      disabled={disabled || submitting}
      onClick={onClickWrapped}
      $invalid={invalid}
      $submitting={submitting}
      $intent={intent}
      $design={design}
      type={type}
      {...props}
    >
      {submitting && (
        <LoadingContainer flex={1} alignItems="center" justifyContent="center">
          <Loading intent={intent} />
        </LoadingContainer>
      )}
      <Content $submitting={submitting}>{children}</Content>
    </DefaultButton>
  );
};

export default Button;
